
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from '../providers/AuthProvider';
import api from '../lib/api';
// import { registerServiceWorker } from '../utils/serviceWorkerRegistration';
import { 
  isNotificationSupported, 
  showPomodoroNotification, 
  requestNotificationPermission,
  getNotificationPermission,
  testNotification
} from '../utils/notificationService';
import { 
  UserSettings, 
  PomodoroState, 
  PomodoroContextType, 
  DEFAULT_POMODORO_STATE,
  PomodoroMode 
} from '../types/pomodoro';
import { userSettingsStorage, pomodoroStateStorage, isLocalStorageAvailable } from '../utils/storage/localStorage';
import eventBus, { EVENTS } from '../utils/eventBus';

const defaultState: PomodoroState = DEFAULT_POMODORO_STATE;

const PomodoroContext = createContext<PomodoroContextType | undefined>(undefined);

export function PomodoroProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const [state, setState] = useState<PomodoroState>(defaultState);
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null);
  const [notificationSupported, setNotificationSupported] = useState<boolean>(false);
  
  // Helper function to save state to localStorage
  const saveStateToLocalStorage = (stateToSave: PomodoroState) => {
    if (isLocalStorageAvailable()) {
      pomodoroStateStorage.set({
        ...stateToSave,
        lastSavedTime: Date.now()
      });
    }
  };
  
  // Helper function to update user settings in localStorage
  const saveUserSettings = (settings: Partial<UserSettings>) => {
    if (isLocalStorageAvailable()) {
      const savedSettings = userSettingsStorage.get() || {};
      userSettingsStorage.set({
        ...savedSettings,
        ...settings
      });
    }
  };

  // Helper function to apply settings
  const applyUserSettingsToState = (settings: Partial<UserSettings>) => {
    if (typeof settings.workDuration === 'number' && settings.workDuration > 0) {
      setWorkDuration(settings.workDuration);
    }
    
    if (typeof settings.breakDuration === 'number' && settings.breakDuration > 0) {
      setBreakDuration(settings.breakDuration);
    }
    
    if (typeof settings.soundNotification === 'boolean') {
      setState(prev => ({
        ...prev,
        soundNotification: settings.soundNotification
      }));
    }
    
    if (typeof settings.webNotification === 'boolean') {
      setState(prev => ({
        ...prev,
        webNotification: settings.webNotification
      }));
    }
  };

  // Fetch completed pomodoros count from API
  const fetchDailyPomodoros = async () => {
    if (isAuthenticated && user?.id) {
      try {
        const userData = await api.auth.me();
        if (userData && userData.daily_completed_pomodoros !== undefined) {
          setState(prev => ({
            ...prev,
            completedPomodoros: userData.daily_completed_pomodoros
          }));
        }
      } catch (error) {
        console.error('Error fetching daily pomodoro count:', error);
      }
    }
  };
  
  // Load user settings from localStorage
  const loadUserSettings = () => {
    try {
      if (isLocalStorageAvailable()) {
        const savedSettings = userSettingsStorage.get();
        if (savedSettings) {
          console.log('Loading settings from localStorage:', savedSettings);
          applyUserSettingsToState(savedSettings);
        }
      }
    } catch (err) {
      console.error('Error loading settings from localStorage:', err);
    }
  };

  // Check notification support
  const loadNotificationPermission = () => { 
    const supported = isNotificationSupported();
    setNotificationSupported(supported);
    
    if (supported) {
      console.log('浏览器支持通知，当前权限:', getNotificationPermission());
    } else {
      console.log('浏览器不支持通知');
    }
  };

  // Helper function to play sound
  const playSound = (mode: PomodoroMode) => {
    try {
      const sound = new Audio(mode === 'work' ? '/sounds/bell.mp3' : '/sounds/bell2.mp3');
      sound.play();
    } catch (error) {
      console.error('Error playing sound:', error);
    }
  };
  
  // Helper function to show notification
  const showNotification = async (mode: PomodoroMode) => {
    if (state.webNotification && notificationSupported) {
      await showPomodoroNotification(mode, { debug: true });
    }
  };

  // Create a new pomodoro record
  const createNewPomodoro = async (status: 'started' | 'paused', type: PomodoroMode = 'work') => {
    if (!isAuthenticated || !user) {
      console.log('Not creating pomodoro - user not authenticated');
      return null;
    }
    
    try {
      // 获取当前任务 ID - 使用最新状态
      const currentTaskId = state.currentTaskId;

      const pomodoroData = {
        task_id: currentTaskId,
        start_time: new Date().toISOString(),
        duration: type === 'work' ? Math.round(state.workDuration / 60) : Math.round(state.breakDuration / 60),
        type: type,
        completed: false,
        status: 'started',
        time_left: state.timeLeft,
        last_saved_time: state.lastSavedTime,
      };
      
      console.log('Creating new pomodoro with task_id:', currentTaskId);
      const response = await api.pomodoros.create(pomodoroData);
      
      console.log('New pomodoro created id:', response.id);
      
      setState(prev => ({
        ...prev,
        currentPomodoroId: response.id
      }));
      
      return response.id;
    } catch (error) {
      console.error('Failed to create pomodoro:', error);
      return null;
    }
  };

  // Update pomodoro status
  const updatePomodoroStatus = async (status: 'started' | 'paused' | 'stopped') => {
    if (!isAuthenticated || !user || !state.currentPomodoroId) {
      console.log('Not updating pomodoro - user not authenticated or no current pomodoro');
      return;
    }
    
    try {
      console.log(`Updating pomodoro ${state.currentPomodoroId} status to ${status}`);
      
      // 获取当前任务 ID - 使用最新状态
      const currentTaskId = state.currentTaskId;
      
      const updateData = {
        completed: status === 'stopped' ? true : undefined,
        end_time: status === 'stopped' ? new Date().toISOString() : undefined,
        status: status === 'stopped' ? 'completed' : status,
        time_left: state.timeLeft,
        last_saved_time: state.lastSavedTime,
        task_id: currentTaskId, // 添加当前任务 ID，确保使用最新的任务关联
      };
      
      console.log('Updating pomodoro with task_id:', currentTaskId);
      await api.pomodoros.update(state.currentPomodoroId, updateData);
      
      console.log('Pomodoro status updated');
    } catch (error) {
      console.error('Failed to update pomodoro status:', error);
    }
  };

  // Update pomodoro completion
  const updatePomodoroCompletion = async (pomodoroIdToUpdate?: string | null, timeLeft?: number | null) => {
    const currentId = pomodoroIdToUpdate || state.currentPomodoroId;

    if (!isAuthenticated || !user || !currentId) {
      if (!currentId) {
        console.log('Not marking pomodoro as complete - currentPomodoroId empty');
      }
      return;
    }
    
    const timeLeftToUpdate = timeLeft !== null && timeLeft !== undefined ? timeLeft : state.timeLeft;
    
    try {
      // 获取当前任务 ID - 使用最新状态
      const currentTaskId = state.currentTaskId;
      
      const updateData = {
        completed: true,
        end_time: new Date().toISOString(),
        time_left: timeLeftToUpdate,
        task_id: currentTaskId // 添加当前任务 ID，确保使用最新的任务关联
      };
      
      console.log('Completing pomodoro with task_id:', currentTaskId);
      await api.pomodoros.update(currentId, updateData);
      
      console.log('Pomodoro marked as complete');
      
      // 如果是工作模式的番茄钟完成，检查是否需要自动完成任务
      if (state.mode === 'work' && currentTaskId) {
        const autoCompleteTask = userSettingsStorage.get()?.autoCompleteTask;
        if (autoCompleteTask) {
          console.log(`Auto-completing task ${currentTaskId} from updatePomodoroCompletion`);
          await completeCurrentTask(currentTaskId);
        }
      }
    } catch (error) {
      console.error('Failed to mark pomodoro as complete:', error);
    }
  };

  // Initialize the app
  useEffect(() => {
    loadUserSettings();
    loadNotificationPermission();
  }, []);

  // Request notification permission when setting changes
  useEffect(() => {
    const requestPermissionIfNeeded = async () => {
      if (state.webNotification && notificationSupported) {
        const permission = getNotificationPermission();
        if (permission !== 'granted' && permission !== 'denied') {
          await requestNotificationPermission();
        }
      }
    };
    
    requestPermissionIfNeeded();
  }, [state.webNotification, notificationSupported]);

  // Load user settings from API
  useEffect(() => {
    const fetchUserSettings = async () => {
      if (isAuthenticated && user?.id) {
        try {
          const settings = await api.settings.get();
          console.log('Settings loaded from API:', settings);
          
          applyUserSettingsToState(settings);
          
          // Save to localStorage
          saveUserSettings({
            workDuration: settings.workDuration,
            breakDuration: settings.breakDuration,
            soundNotification: settings.soundNotification,
            webNotification: (settings as any).webNotification ?? true
          });
        } catch (error) {
          console.error('Error fetching user settings:', error);
        }
      }
    };
    
    fetchUserSettings();
  }, [isAuthenticated, user]);

  // Restore state from localStorage
  useEffect(() => {
    if (isLocalStorageAvailable()) {
      const parsedState = pomodoroStateStorage.get();
      if (parsedState) {
        try {
          // Validate key fields
          if (isNaN(parsedState.timeLeft) || parsedState.timeLeft <= 0) {
            parsedState.timeLeft = parsedState.mode === 'work' 
              ? parsedState.workDuration 
              : parsedState.breakDuration;
          }
          
          if (isNaN(parsedState.workDuration) || parsedState.workDuration <= 0) {
            parsedState.workDuration = 25 * 60;
          }
          
          if (isNaN(parsedState.breakDuration) || parsedState.breakDuration <= 0) {
            parsedState.breakDuration = 5 * 60;
          }
          
          // 离开这一页/这个标签页期间流掉的时间要算数。
          //
          // 旧代码在这里算出了 elapsedSeconds 却从来没减掉（`timeLeft = timeLeft`
          // 是个空操作），然后强制把运行中的计时器改成暂停——于是「离开页面
          // 计时就停了」。现在按真实流逝扣减，归零就交给下面那个完成 effect
          // 去走正常的完成流程（响铃、通知、落库、切模式）。
          //
          // 只对**正在跑**的计时器扣减：暂停中的本来就该冻住。旧代码对暂停态
          // 也走同一条路，离开得比剩余时间久还会把它整个重置掉。
          if (parsedState.isActive && !parsedState.isPaused && parsedState.lastSavedTime) {
            const elapsedSeconds = Math.floor((Date.now() - parsedState.lastSavedTime) / 1000);
            parsedState.timeLeft = Math.max(0, parsedState.timeLeft - elapsedSeconds);
          }
          
          // Convert date strings to Date objects
          if (parsedState.startTime) parsedState.startTime = new Date(parsedState.startTime);
          if (parsedState.pauseTime) parsedState.pauseTime = new Date(parsedState.pauseTime);
          
          setState(parsedState);
        } catch (error) {
          console.error('Failed to parse saved pomodoro state:', error);
        }
      }
    }
    
    // Fetch pomodoro count from API
    fetchDailyPomodoros();
  }, []);

  // Save state to localStorage when it changes
  useEffect(() => {
    saveStateToLocalStorage(state);
  }, [state]);

  // 计时循环。
  //
  // 以前它长在 startTimer() 里，于是只有「人点了开始」这一条路能让它跑起来。
  // provider 一旦重新挂载（刷新、换页），状态是「在跑」但没有任何东西在走，
  // 所以旧代码只好在恢复时强制暂停。改成由状态驱动之后，「在跑」这件事本身
  // 就会把循环带起来，恢复时不必再撒这个谎。
  //
  // 倒计时按墙上时钟算，不是每秒减一：浏览器会把后台标签页的 setInterval
  // 限流到大约每分钟一次，减一法在那种情况下会越走越慢，而人回到页面时
  // 看到的数字就是错的。
  const deadlineRef = useRef<number | null>(null);

  useEffect(() => {
    if (!state.isActive || state.isPaused) {
      deadlineRef.current = null;
      return;
    }

    // 只在「开始跑」这一刻读一次 timeLeft 定下终点，之后每次 tick 都从终点
    // 倒推——所以 timeLeft 不在依赖里，否则每秒都会重建一次循环。
    const deadline = Date.now() + state.timeLeft * 1000;
    deadlineRef.current = deadline;

    const id = setInterval(() => {
      const left = Math.max(0, Math.round((deadline - Date.now()) / 1000));
      setState((prev) => (prev.timeLeft === left ? prev : { ...prev, timeLeft: left }));
    }, 1000);

    // 仍然把 id 放进 timerId：下面那些 clearInterval(timerId) 的调用点全都
    // 同时改了 isActive/isPaused，cleanup 会再清一次，重复清是安全的。
    setTimerId(id);
    return () => {
      clearInterval(id);
      setTimerId(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isActive, state.isPaused, state.mode]);


  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [timerId]);

  // Timer control functions
  const startTimer = () => {
    console.log(`Starting timer: mode=${state.mode}, isActive=${state.isActive}, isPaused=${state.isPaused}`);
    
    if (!state.isActive || state.isPaused) {
      const now = new Date();
      setState((prev) => ({ 
        ...prev, 
        isActive: true, 
        isPaused: false,
        startTime: prev.startTime || now,
        pauseTime: null
      }));
      
      let pomodoroId = state.currentPomodoroId;
      const wasInactive = !state.isActive && !state.isPaused;
      
      // Setup pomodoro
      const setupPomodoro = async () => {
        if (state.isPaused && state.currentPomodoroId) {
          await updatePomodoroStatus('started');
        } else if (!state.currentPomodoroId) {
          // For work mode, always create a Pomodoro record
          // For break mode, only create when user manually clicks "Start Break" button
          if (state.mode === 'work' || (state.mode === 'break' && wasInactive)) {
            pomodoroId = await createNewPomodoro('started', state.mode);
          }
        }
      };
      
      setupPomodoro();
      
      // 计时循环不在这里起——见下面那个「计时循环」effect。
      // 这里只负责把状态改成「在跑」，循环由状态驱动。
    }
  };

  // Watch for timer completion - separate from the interval
  useEffect(() => {
    // Check if timer has completed (reached zero)
    if (state.isActive && !state.isPaused && state.timeLeft === 0) {
      console.log('Timer completed outside of interval: mode=' + state.mode);
      
      // Clear the interval
      if (timerId) {
        clearInterval(timerId);
        setTimerId(null);
      }
      
      // Handle completion based on current mode
      const currentMode = state.mode;
      
      // Play sound notification
      if (state.soundNotification) {
        playSound(currentMode === 'work' ? 'break' : 'work');
      }
      
      // Show notification
      if (state.webNotification) {
        showNotification(currentMode);
      }
      
      // Update pomodoro completion status
      if (state.currentPomodoroId) {
        updatePomodoroCompletion(state.currentPomodoroId, 0)
          .then(() => {
            if (currentMode === 'work') {
              fetchDailyPomodoros();
              
              // Check if we should auto-complete the task
              const autoCompleteTask = userSettingsStorage.get()?.autoCompleteTask;
              if (autoCompleteTask && state.currentTaskId) {
                completeCurrentTask(state.currentTaskId);
              }
            }
          });
      }
      
      // Transition to the next state
      if (currentMode === 'work') {
        setState(prev => ({
          ...prev,
          isActive: false, // Explicitly NOT active - must be manually started
          isPaused: false,
          mode: 'break',
          timeLeft: prev.breakDuration,
          completedPomodoros: prev.completedPomodoros + 1,
          currentPomodoroId: null,
          startTime: null,
          pauseTime: null,
          accumulatedTime: 0
        }));
      } else {
        setState(prev => ({
          ...prev,
          isActive: false, // Explicitly NOT active - must be manually started
          isPaused: false,
          mode: 'work',
          timeLeft: prev.workDuration,
          currentPomodoroId: null,
          startTime: null,
          pauseTime: null,
          accumulatedTime: 0
        }));
      }
    }
  }, [state.timeLeft, state.isActive, state.isPaused]);

  // Helper function to complete the current task
  const completeCurrentTask = async (taskId: number) => {
    if (!isAuthenticated || !taskId) {
      console.log('Not completing task - user not authenticated or no task ID');
      return;
    }
    
    try {
      console.log(`Auto-completing task ${taskId}`);
      const updatedTask = await api.tasks.update(taskId, { completed: true });
      console.log('Task marked as complete');
      
      // Reset the current task ID after completion
      setState(prev => ({
        ...prev,
        currentTaskId: null
      }));
      
      // 发出任务完成事件，通知其他组件更新 UI
      eventBus.emit(EVENTS.TASK_COMPLETED, updatedTask);
    } catch (error) {
      console.error('Failed to auto-complete task:', error);
    }
  };

  const pauseTimer = () => {
    if (state.isActive && !state.isPaused) {
      if (timerId) clearInterval(timerId);
      setTimerId(null);
      
      // Calculate accumulated time
      let newAccumulatedTime = state.accumulatedTime;
      if (state.startTime) {
        const now = new Date();
        const elapsedSinceStart = Math.floor((now.getTime() - state.startTime.getTime()) / 1000);
        newAccumulatedTime += elapsedSinceStart;
      }
      
      setState((prev) => ({ 
        ...prev, 
        isPaused: true,
        pauseTime: new Date(),
        accumulatedTime: newAccumulatedTime
      }));
      
      updatePomodoroStatus('paused');
    }
  };

  const resetTimer = () => {
    if (timerId) clearInterval(timerId);
    setTimerId(null);
    
    if (state.isActive && state.currentPomodoroId) {
      updatePomodoroStatus('stopped');
    }
    
    setState((prev) => ({
      ...prev,
      isActive: false,
      isPaused: false,
      timeLeft: prev.mode === 'work' ? prev.workDuration : prev.breakDuration,
      currentPomodoroId: null,
      startTime: null,
      pauseTime: null,
      accumulatedTime: 0
    }));
  };

  const skipToBreak = () => {
    if (timerId) clearInterval(timerId);
    setTimerId(null);
    
    const isFromWorkMode = state.mode === 'work';
    
    if (isFromWorkMode && state.currentPomodoroId) {
      updatePomodoroCompletion()
        .then(() => fetchDailyPomodoros());
    }
    
    setState((prev) => ({
      ...prev,
      isActive: false,
      isPaused: false,
      mode: 'break',
      timeLeft: prev.breakDuration,
      completedPomodoros: isFromWorkMode ? prev.completedPomodoros + 1 : prev.completedPomodoros,
      currentPomodoroId: null,
      startTime: null,
      pauseTime: null,
    }));
  };

  const skipToWork = () => {
    if (timerId) clearInterval(timerId);
    setTimerId(null);
    
    if (state.mode === 'break' && state.currentPomodoroId) {
      updatePomodoroCompletion();
    }
    
    setState((prev) => ({
      ...prev,
      isActive: false,
      isPaused: false,
      mode: 'work',
      timeLeft: prev.workDuration,
      currentPomodoroId: null,
      startTime: null,
      pauseTime: null,
    }));
  };

  const completePomodoro = () => {
    if (state.mode === 'work') {
      if (timerId) clearInterval(timerId);
      setTimerId(null);
      
      updatePomodoroCompletion()
        .then(() => fetchDailyPomodoros());
      
      setState((prev) => ({
        ...prev,
        isActive: false,
        isPaused: false,
        mode: 'break',
        timeLeft: prev.breakDuration,
        completedPomodoros: prev.completedPomodoros + 1,
        currentPomodoroId: null,
        startTime: null,
        pauseTime: null,
      }));
      
      if (state.soundNotification) {
        playSound('break');
      }
      
      if (state.webNotification) {
        showNotification('work');
      }
    }
  };

  // Settings functions
  const setWorkDuration = (minutes: number) => {
    const seconds = minutes * 60;
    
    setState((prev) => {
      const newTimeLeft = (!prev.isActive && prev.mode === 'work') ? seconds : prev.timeLeft;
      return {
        ...prev,
        workDuration: seconds,
        timeLeft: newTimeLeft
      };
    });
    
    saveUserSettings({ workDuration: minutes });
  };

  const setBreakDuration = (minutes: number) => {
    const seconds = minutes * 60;
    
    setState((prev) => {
      const newTimeLeft = (!prev.isActive && prev.mode === 'break') ? seconds : prev.timeLeft;
      return {
        ...prev,
        breakDuration: seconds,
        timeLeft: newTimeLeft
      };
    });
    
    saveUserSettings({ breakDuration: minutes });
  };

  const setCurrentTaskId = (taskId: number | null) => {
    // 立即更新状态
    setState((prev) => ({
      ...prev,
      currentTaskId: taskId
    }));
    
    // 如果有正在进行的番茄钟，更新其任务关联
    if (state.currentPomodoroId && isAuthenticated) {
      const updatePomodoroTaskId = async () => {
        try {
          console.log(`Directly updating pomodoro ${state.currentPomodoroId} task_id to ${taskId}`);
          
          await api.pomodoros.update(state.currentPomodoroId, {
            task_id: taskId
          });
          
          console.log('Pomodoro task_id updated directly');
        } catch (error) {
          console.error('Failed to update pomodoro task_id directly:', error);
        }
      };
      
      updatePomodoroTaskId();
    }
  };

  const toggleWebNotification = () => {
    setState(prev => {
      const newValue = !prev.webNotification;
      
      if (newValue && notificationSupported) {
        console.log('Web通知已启用，正在测试...');
        setTimeout(async () => {
          testNotification(true, { isEnablingNotification: true });
        }, 100);
      }
      
      saveUserSettings({ webNotification: newValue });
      
      return {
        ...prev,
        webNotification: newValue
      };
    });
  };

  const contextValue: PomodoroContextType = {
    state,
    startTimer,
    pauseTimer,
    resetTimer,
    skipToBreak,
    skipToWork,
    completePomodoro,
    setWorkDuration,
    setBreakDuration,
    setCurrentTaskId,
    toggleWebNotification
  };

  return (
    <PomodoroContext.Provider value={contextValue}>
      {children}
    </PomodoroContext.Provider>
  );
}

export function usePomodoro() {
  const context = useContext(PomodoroContext);
  if (context === undefined) {
    throw new Error('usePomodoro must be used within a PomodoroProvider');
  }
  return context;
} 